import { AbstractDialog } from "../abstract_dialog.js";
import { DialogId } from "../dialog_id.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { RoundedRectangle } from "../../common_ui_elements/rounded_rectangle.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { Vector2 } from "../../../common/vector2.js";
import { getLinkedSocialProviders, LinkedSocialProvider, toAuthProvider } from "../account_providers_preview_state.js";
import { DialogManager } from "../dialog_manager.js";
import { ScreenManager, Screens } from "../../../screen_manager/screen_manager.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { AccountProgressSavedDialog } from "./account_progress_saved_dialog.js";
import { ConnectAccountDialog } from "./connect_account_dialog.js";
import { TwoAccountsDialog } from "./two_accounts_dialog.js";
import { AuthMessageDialog } from "./auth_message_dialog.js";
import { linkProvider, currentAccountProvider, LinkOutcome } from "../../../api/account_link_service.js";


export class SaveDialog extends AbstractDialog
{
  public static readonly dialog_id = DialogId.SAVE_DIALOG;
  private static readonly BUTTON_Y_SLOTS = [ 549, 723, 891 ];
  private bg!: UINineSliceElement;
  private close_btn!: UIImage;
  private visible_buttons: LinkedSocialProvider[] = [];
  private is_linking = false;

  constructor(
    private on_google_linked?: () => void,
    private hidden_buttons: LinkedSocialProvider[] = getLinkedSocialProviders()
  )
  {
    const body_w = LayoutScaler.widthFromDesignAdaptive( 909 );
    const body_h = LayoutScaler.heightFromDesign( 1100 );

    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;

    super( DialogManager.ctx, body_x, body_y, body_w, body_h );
    this.visible_buttons = SaveDialog.getVisibleButtons( hidden_buttons );

    this.createBackground();
    this.createTitle();
    this.createText();
    this.createGoogleButton();
    this.createFacebookButton();
    this.createAppleButton();
    this.createCloseButton();
  }

  private static getVisibleButtons( hidden_buttons: LinkedSocialProvider[] = [] ): LinkedSocialProvider[]
  {
    return [
      LinkedSocialProvider.Google,
      LinkedSocialProvider.Facebook,
      LinkedSocialProvider.Apple
    ].filter( button => !hidden_buttons.includes( button ) );
  }

  private getButtonY( button: LinkedSocialProvider ): number
  {
    const index = this.visible_buttons.indexOf( button );
    return LayoutScaler.yFromDesign( SaveDialog.BUTTON_Y_SLOTS[ index ] );
  }

  private completePreviewLink(): void
  {
    ScreenManager.showScreen( Screens.MENU );
    DialogManager.closeAllDialogs();
    DialogManager.openDialog( new AccountProgressSavedDialog() );
  }

  private async handleLogin( provider: LinkedSocialProvider ): Promise<void>
  {
    if ( this.is_linking ) return;
    this.is_linking = true;
    try
    {
      const outcome = await linkProvider( provider );
      this.handleOutcome( provider, outcome );
    }
    finally
    {
      this.is_linking = false;
    }
  }

  private handleOutcome( provider: LinkedSocialProvider, outcome: LinkOutcome ): void
  {
    switch ( outcome.kind )
    {
      case 'linked':
        if ( provider === LinkedSocialProvider.Google ) this.on_google_linked?.();
        this.completePreviewLink();
        return;

      case 'cancelled':
        return;

      case 'conflict':
        DialogManager.openDialog( new TwoAccountsDialog( {
          current_player:    outcome.conflict.current_player,
          current_provider:  currentAccountProvider( outcome.conflict.current_player ),
          existing_player:   outcome.conflict.existing_player,
          existing_provider: toAuthProvider( provider ),
          on_choose: async ( choice ) =>
          {
            const next = await outcome.resolve( choice );
            this.handleOutcome( provider, next );
          },
        } ) );
        return;

      case 'error':
        DialogManager.openDialog( AuthMessageDialog.forLinkError( provider, outcome.reason ) );
        return;
    }
  }

  private createBackground(): void
  {
    this.bg = new UINineSliceElement(
      this.ctx,
      LayoutScaler.xFromDesign( 0 ),
      LayoutScaler.yFromDesign( 0 ),
      this.rect.getW(),
      this.rect.getH(),
      ImgID.UI_CapsuleBlue,
      "capsule_bg"
    );
    this.addChild( this.bg );
  }

  private createCloseButton(): void
  {
    const design_btn_size = 117;
    const design_offset_x = 10;
    const design_offset_y = 100;

    const btn_size = LayoutScaler.widthFromDesign( design_btn_size );
    const btn_x = this.rect.getW() - btn_size + LayoutScaler.widthFromDesign( design_offset_x );
    const btn_y = -LayoutScaler.yFromDesign( design_offset_y );

    this.close_btn = new UIImage(
      this.ctx,
      btn_x,
      btn_y,
      btn_size,
      btn_size,
      ImgID.UI_SettingsDialogCloseBtn,
      {},
      true
    );

    this.close_btn.setOnClick( () => this.close() );
    this.addChild( this.close_btn );
  }

  protected override playAppearAnimation(
    from_scale      = 0.7,
    overshoot_scale = 1.1,
    final_scale     = 1.0,
    scale_up_duration = 136,
    settle_duration   = 96
  ): void
  {
    this.close_btn.setScale( new Vector2( 0, 0 ) );

    super.playAppearAnimation( from_scale, overshoot_scale, final_scale, scale_up_duration, settle_duration );

    this.popCloseButtonAfterAppear( this.close_btn, scale_up_duration + settle_duration );
  }

  private createTitle(): void
  {
    const design_h = 154;
    const design_y = 102;
    const design_font_size = 70;
    const design_outline_w = 20;

    const label_w = this.rect.getW() * 0.8;
    const label_h = LayoutScaler.heightFromDesign( design_h );
    const label_x = this.rect.getW2() - label_w / 2;
    const label_y = LayoutScaler.yFromDesign( design_y );

    const label = new UITextElement(
      this.ctx,
      label_x,
      label_y,
      label_w,
      label_h,
      {
        localization_key: "SAVE_DIALOG.TITLE",
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.geologica,
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 700,
        auto_wrap: true,
        outline_width: LayoutScaler.textBorderSizeFromDesign( design_outline_w ),
        outline_color: ColorScheme.booster_dialog_title_outline,
      }
    );

    this.addChild( label );
  }

  private createText(): void
  {
    const design_w = 760;
    const design_h = 240;
    const design_y = 278;
    const design_font_size = 50;
    const design_outline_w = 10;

    const label_w = LayoutScaler.widthFromDesign( design_w );
    const label_h = LayoutScaler.heightFromDesign( design_h );
    const label_x = this.rect.getW2() - label_w / 2;
    const label_y = LayoutScaler.yFromDesign( design_y );

    const label = new UITextElement(
      this.ctx,
      label_x,
      label_y,
      label_w,
      label_h,
      {
        localization_key: "SAVE_DIALOG.TEXT_TWO",
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.geologica,
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 700,
        auto_wrap: true,
        outline_width: LayoutScaler.textBorderSizeFromDesign( design_outline_w ),
        outline_color: ColorScheme.settings_dialog_title_outline,
      }
    );

    this.addChild( label );
  }

  private createGoogleButton(): void
  {
    if ( !this.visible_buttons.includes( LinkedSocialProvider.Google ) ) return;

    const design_btn_w = 757;
    const design_btn_h = 140;
    const design_corner_r = 70;
    const design_icon_offset = 20;
    const design_icon_size = 100;
    const design_text_gap = 30;
    const design_text_margin = 40;
    const design_font_size = 47;

    const btn_bg_y = this.getButtonY( LinkedSocialProvider.Google );
    const btn_bg_w = LayoutScaler.widthFromDesign( design_btn_w );
    const btn_bg_x = ( this.rect.getW() - btn_bg_w ) / 2;
    const btn_bg_h = LayoutScaler.heightFromDesign( design_btn_h );

    const google_btn_bg = new RoundedRectangle(
      this.ctx,
      btn_bg_x,
      btn_bg_y,
      btn_bg_w,
      btn_bg_h,
      LayoutScaler.cornerRadiusFromDesign( design_corner_r ),
      "#D6E8EE",
      undefined,
      true
    );

    const google_icon_x = LayoutScaler.xFromDesign( design_icon_offset );
    const google_icon_y = LayoutScaler.yFromDesign( design_icon_offset );
    const google_icon_size = LayoutScaler.widthFromDesign( design_icon_size );

    const google_icon = new UIImage(
      this.ctx,
      google_icon_x,
      google_icon_y,
      google_icon_size,
      google_icon_size,
      ImgID.GoogleIcon
    );

    const google_text_x = google_icon_x + google_icon_size + LayoutScaler.xFromDesign( design_text_gap );
    const google_text_w = btn_bg_w - LayoutScaler.xFromDesign( design_text_margin ) - google_text_x;
    const google_text_h = btn_bg_h;

    const google_text = new UITextElement(
      this.ctx,
      google_text_x,
      0,
      google_text_w,
      google_text_h,
      {
        localization_key: "SAVE_DIALOG.SIGN_WITH_GOOGLE",
        text_align: MyTextAlign.Center,
        text_color: "#2B394B",
        text_style: TextStyleScheme.geologica,
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 700,
        vertical_align: MyTextVerticalAlign.Center,
        using_y_offset: false,
        ...TextStyleScheme.buttonShadow(),
      }
    );

    google_btn_bg.addChild( google_icon, true );
    google_btn_bg.addChild( google_text, true );
    this.addChild( google_btn_bg );

    google_btn_bg.setOnClick( () =>
    {
      DialogManager.openDialog( new ConnectAccountDialog( LinkedSocialProvider.Google, () => { void this.handleLogin( LinkedSocialProvider.Google ); } ) );
    } );
  }

  private createFacebookButton(): void
  {
    if ( !this.visible_buttons.includes( LinkedSocialProvider.Facebook ) ) return;

    const design_btn_w = 757;
    const design_btn_h = 140;
    const design_corner_r = 70;
    const design_icon_offset = 20;
    const design_icon_size = 100;
    const design_text_gap = 30;
    const design_text_margin = 40;
    const design_font_size = 47;

    const btn_bg_y = this.getButtonY( LinkedSocialProvider.Facebook );
    const btn_bg_w = LayoutScaler.widthFromDesign( design_btn_w );
    const btn_bg_x = ( this.rect.getW() - btn_bg_w ) / 2;
    const btn_bg_h = LayoutScaler.heightFromDesign( design_btn_h );

    const facebook_btn_bg = new RoundedRectangle(
      this.ctx,
      btn_bg_x,
      btn_bg_y,
      btn_bg_w,
      btn_bg_h,
      LayoutScaler.cornerRadiusFromDesign( design_corner_r ),
      "#2173F6",
      undefined,
      true
    );

    const facebook_icon_x = LayoutScaler.xFromDesign( design_icon_offset );
    const facebook_icon_y = LayoutScaler.yFromDesign( design_icon_offset );
    const facebook_icon_size = LayoutScaler.widthFromDesign( design_icon_size );

    const facebook_icon = new UIImage(
      this.ctx,
      facebook_icon_x,
      facebook_icon_y,
      facebook_icon_size,
      facebook_icon_size,
      ImgID.FacebookIcon
    );

    const facebook_text_x = facebook_icon_x + facebook_icon_size + LayoutScaler.xFromDesign( design_text_gap );
    const facebook_text_w = btn_bg_w - LayoutScaler.xFromDesign( design_text_margin ) - facebook_text_x;
    const facebook_text_h = btn_bg_h;

    const facebook_text = new UITextElement(
      this.ctx,
      facebook_text_x,
      0,
      facebook_text_w,
      facebook_text_h,
      {
        localization_key: "SAVE_DIALOG.SIGN_WITH_FACEBOOK",
        text_align: MyTextAlign.Center,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 700,
        vertical_align: MyTextVerticalAlign.Center,
        using_y_offset: false,
        ...TextStyleScheme.buttonShadow(),
      }
    );

    facebook_btn_bg.addChild( facebook_icon, true );
    facebook_btn_bg.addChild( facebook_text, true );
    this.addChild( facebook_btn_bg );

    facebook_btn_bg.setOnClick( () =>
    {
      DialogManager.openDialog( new ConnectAccountDialog( LinkedSocialProvider.Facebook, () => { void this.handleLogin( LinkedSocialProvider.Facebook ); } ) );
    } );
  }

  private createAppleButton(): void
  {
    if ( !this.visible_buttons.includes( LinkedSocialProvider.Apple ) ) return;

    const design_btn_w = 757;
    const design_btn_h = 140;
    const design_corner_r = 70;
    const design_icon_offset = 20;
    const design_icon_size = 100;
    const design_text_gap = 30;
    const design_text_margin = 40;
    const design_font_size = 47;

    const btn_bg_y = this.getButtonY( LinkedSocialProvider.Apple );
    const btn_bg_w = LayoutScaler.widthFromDesign( design_btn_w );
    const btn_bg_x = ( this.rect.getW() - btn_bg_w ) / 2;
    const btn_bg_h = LayoutScaler.heightFromDesign( design_btn_h );

    const apple_btn_bg = new RoundedRectangle(
      this.ctx,
      btn_bg_x,
      btn_bg_y,
      btn_bg_w,
      btn_bg_h,
      LayoutScaler.cornerRadiusFromDesign( design_corner_r ),
      "#242424",
      undefined,
      true
    );

    const apple_icon_x = LayoutScaler.xFromDesign( design_icon_offset );
    const apple_icon_y = LayoutScaler.yFromDesign( design_icon_offset );
    const apple_icon_size = LayoutScaler.widthFromDesign( design_icon_size );

    const apple_icon = new UIImage(
      this.ctx,
      apple_icon_x,
      apple_icon_y,
      apple_icon_size,
      apple_icon_size,
      ImgID.AppleIcon
    );

    const apple_text_x = apple_icon_x + apple_icon_size + LayoutScaler.xFromDesign( design_text_gap );
    const apple_text_w = btn_bg_w - LayoutScaler.xFromDesign( design_text_margin ) - apple_text_x;
    const apple_text_h = btn_bg_h;

    const apple_text = new UITextElement(
      this.ctx,
      apple_text_x,
      0,
      apple_text_w,
      apple_text_h,
      {
        localization_key: "SAVE_DIALOG.SIGN_WITH_APPLE",
        text_align: MyTextAlign.Center,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 700,
        vertical_align: MyTextVerticalAlign.Center,
        using_y_offset: false,
        ...TextStyleScheme.buttonShadow(),
      }
    );

    apple_btn_bg.addChild( apple_icon, true );
    apple_btn_bg.addChild( apple_text, true );
    this.addChild( apple_btn_bg );

    apple_btn_bg.setOnClick( () =>
    {
      DialogManager.openDialog( new ConnectAccountDialog( LinkedSocialProvider.Apple, () => { void this.handleLogin( LinkedSocialProvider.Apple ); } ) );
    } );
  }

  public name(): string
  {
    return "SaveDialog";
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }

  // public getBackgroundImageId(): ImgID | null
  // {
  //   return ImgID.UI_SaveDialogBg;
  // }
}
